import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Star, Volume2, VolumeX, ChefHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";
import { KitchenScene3D } from "@/components/Scene3D";

import { getVoiceLang } from "@/utils/voicePref";
const RECIPES = [
  {
    id: 1, name: "Level 1 - Jus Stroberi", result: "\uD83E\uDD64", resultName: "Jus Stroberi",
    steps: [
      { instruction: "Ambil Stroberi!", correct: "\uD83C\uDF53", correctName: "Strawberry" },
      { instruction: "Masukkan ke Blender!", correct: "\uD83E\uDED7", correctName: "Blender" },
      { instruction: "Tekan Blend!", correct: "\uD83D\uDD34", correctName: "Blend" },
    ],
    ingredients: ["\uD83C\uDF53", "\uD83C\uDF4C", "\uD83E\uDED7", "\uD83C\uDF4E", "\uD83D\uDD34", "\uD83C\uDF4A"],
  },
  {
    id: 2, name: "Level 2 - Sandwich", result: "\uD83E\uDD6A", resultName: "Sandwich",
    steps: [
      { instruction: "Ambil Roti!", correct: "\uD83C\uDF5E", correctName: "Bread" },
      { instruction: "Tambah Keju!", correct: "\uD83E\uDDC0", correctName: "Cheese" },
      { instruction: "Tambah Selada!", correct: "\uD83E\uDD6C", correctName: "Lettuce" },
    ],
    ingredients: ["\uD83C\uDF5E", "\uD83E\uDDC0", "\uD83E\uDD6C", "\uD83C\uDF45", "\uD83E\uDD5A", "\uD83C\uDF36\uFE0F"],
  },
  {
    id: 3, name: "Level 3 - Sup Wortel", result: "\uD83C\uDF72", resultName: "Sup Wortel",
    steps: [
      { instruction: "Ambil Wortel!", correct: "\uD83E\uDD55", correctName: "Carrot" },
      { instruction: "Masukkan ke Mangkuk!", correct: "\uD83C\uDF5C", correctName: "Bowl" },
      { instruction: "Aduk dengan Sendok!", correct: "\uD83E\uDD44", correctName: "Spoon" },
    ],
    ingredients: ["\uD83E\uDD55", "\uD83C\uDF5C", "\uD83E\uDD44", "\uD83C\uDF3D", "\uD83E\uDD54", "\uD83C\uDF46"],
  },
  {
    id: 4, name: "Level 4 - Kue Pelangi", result: "\uD83C\uDF82", resultName: "Kue Pelangi",
    steps: [
      { instruction: "Ambil Tepung!", correct: "\uD83C\uDF30", correctName: "Flour" },
      { instruction: "Tambah Telur!", correct: "\uD83E\uDD5A", correctName: "Egg" },
      { instruction: "Pilih Topping Merah!", correct: "\uD83D\uDD34", correctName: "Red" },
      { instruction: "Pilih Topping Biru!", correct: "\uD83D\uDD35", correctName: "Blue" },
    ],
    ingredients: ["\uD83C\uDF30", "\uD83E\uDD5A", "\uD83D\uDD34", "\uD83D\uDD35", "\uD83D\uDFE2", "\uD83D\uDFE1", "\uD83E\uDDC2", "\uD83C\uDF6C"],
  },
  {
    id: 5, name: "Level 5 - Pizza & Jus", result: "\uD83C\uDF55", resultName: "Pizza & Jus",
    steps: [
      { instruction: "Ambil Adonan Pizza!", correct: "\uD83E\uDD68", correctName: "Dough" },
      { instruction: "Tambah Saus Tomat!", correct: "\uD83C\uDF45", correctName: "Tomato" },
      { instruction: "Tambah Keju!", correct: "\uD83E\uDDC0", correctName: "Cheese" },
      { instruction: "Ambil Jeruk untuk Jus!", correct: "\uD83C\uDF4A", correctName: "Orange" },
      { instruction: "Blend Jus Jeruk!", correct: "\uD83D\uDD34", correctName: "Blend" },
    ],
    ingredients: ["\uD83E\uDD68", "\uD83C\uDF45", "\uD83E\uDDC0", "\uD83C\uDF4A", "\uD83D\uDD34", "\uD83C\uDF36\uFE0F", "\uD83E\uDD55", "\uD83C\uDF4C"],
  },
];

const playChopSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 500; osc.type = "sine";
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.08);
    setTimeout(() => {
      const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
      o2.connect(g2); g2.connect(ctx.destination);
      o2.frequency.value = 700; o2.type = "sine";
      g2.gain.setValueAtTime(0.2, ctx.currentTime); g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
      o2.start(ctx.currentTime); o2.stop(ctx.currentTime + 0.06);
    }, 80);
  } catch (err) { logger.debug("game op failed", err); }
};

const playWrongSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 200; osc.type = "square";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
  } catch (err) { logger.debug("game op failed", err); }
};

const playClapSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784, 1047].forEach((f, i) => {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = f; osc.type = "sine";
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.2);
      osc.start(ctx.currentTime + i * 0.12); osc.stop(ctx.currentTime + i * 0.12 + 0.2);
    });
  } catch (err) { logger.debug("game op failed", err); }
};

const speakText = (text) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0; u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const MasakBareng = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [currentLevel, setCurrentLevel] = useState(0);
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => {
    const saved = loadPlayerNames();
    return [saved[0] || "", saved[1] || ""];
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [completedItems, setCompletedItems] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [wrongItem, setWrongItem] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(0); // 0 = P1 picks, 1 = P2 places
  const [selectedItem, setSelectedItem] = useState(null);

  const recipe = RECIPES[currentLevel];
  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startLevel = (lvlIdx) => {
    savePlayerNames(playerNames);
    setCurrentLevel(lvlIdx);
    setCurrentStep(0);
    setCompletedItems([]);
    setWrongItem(null);
    setFeedback(null);
    setCurrentPlayer(0);
    setSelectedItem(null);
    setGameState("playing");
    // Voice: announce recipe
    if (soundOn) {
      setTimeout(() => speakText(`Let's cook ${RECIPES[lvlIdx].resultName}!`), 500);
      setTimeout(() => speakText(`${RECIPES[lvlIdx].steps[0].instruction}`), 2000);
    }
  };

  // Voice guidance for each new step
  useEffect(() => {
    if (gameState !== "playing") return;
    if (currentStep > 0 && soundOn) {
      const step = recipe.steps[currentStep];
      setTimeout(() => speakText(`Step ${currentStep + 1}. ${step.instruction}`), 300);
    }
  }, [currentStep, gameState, recipe, soundOn]);

  const handleIngredientClick = useCallback((emoji) => {
    if (gameState !== "playing") return;

    const step = recipe.steps[currentStep];

    // Player 1 picks ingredient
    if (currentPlayer === 0) {
      setSelectedItem(emoji);
      setCurrentPlayer(1);
      if (soundOn) {
        speakText(`${p1} is choosing. Turn ${p2}, confirm ingredient!`);
      }
      return;
    }

    // Player 2 places ingredient
    if (currentPlayer === 1) {
      const itemToCheck = selectedItem || emoji;

      if (itemToCheck === step.correct) {
        // Correct!
        if (soundOn) {
          playChopSound();
          setTimeout(() => speakText(`Nice! ${step.correctName} added!`), 300);
        }
        setCompletedItems(prev => [...prev, itemToCheck]);
        setFeedback({ type: "correct", text: "Benar!" });
        setSelectedItem(null);
        setCurrentPlayer(0);

        setTimeout(() => setFeedback(null), 800);

        if (currentStep + 1 >= recipe.steps.length) {
          // All steps done!
          setTimeout(() => {
            setGameState("won");
            recordGameWon("masak");
            if (soundOn) {
              playClapSound();
              setTimeout(() => speakText(`Great! ${p1} and ${p2} cooked ${recipe.resultName}! Food is ready!`), 500);
            }
          }, 600);
        } else {
          setCurrentStep(prev => prev + 1);
        }
      } else {
        // Wrong!
        if (soundOn) {
          playWrongSound();
          setTimeout(() => speakText(`Not that one! Try again! ${step.instruction}`), 300);
        }
        setWrongItem(itemToCheck);
        setFeedback({ type: "wrong", text: "Coba lagi!" });
        setSelectedItem(null);
        setCurrentPlayer(0);
        setTimeout(() => { setWrongItem(null); setFeedback(null); }, 1200);
      }
    }
  }, [gameState, recipe, currentStep, currentPlayer, selectedItem, soundOn, p1, p2]);

  // MENU
  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="masak-bareng-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ rotate: [0, -10, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            🍳
          </motion.span>
          <h1 className="font-fredoka text-3xl sm:text-4xl text-gold font-bold mb-2">Masak Bareng</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">
            Kerja sama bikin makanan bersama teman!
          </p>
          <div className="glass-card rounded-xl p-3 mb-4 text-left">
            <p className="font-fredoka text-gold-light/70 text-xs mb-1 font-bold">Cara Bermain:</p>
            <p className="font-fredoka text-gold-light/60 text-xs"><span className="text-cyan-400 font-bold">{p1}</span>: Pilih bahan yang benar</p>
            <p className="font-fredoka text-gold-light/60 text-xs"><span className="text-pink-400 font-bold">{p2}</span>: Konfirmasi & masukkan ke wadah</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="font-fredoka text-cyan-400 text-xs font-bold block mb-1">Chef 1</label>
              <input data-testid="masak-p1-name" type="text" value={playerNames[0]}
                onChange={(e) => setPlayerNames(prev => [e.target.value, prev[1]])}
                placeholder="Chef 1" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-cyan-400/50" />
            </div>
            <div>
              <label className="font-fredoka text-pink-400 text-xs font-bold block mb-1">Chef 2</label>
              <input data-testid="masak-p2-name" type="text" value={playerNames[1]}
                onChange={(e) => setPlayerNames(prev => [prev[0], e.target.value])}
                placeholder="Chef 2" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-pink-400/50" />
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2 w-full mb-4">
          {RECIPES.map((r, idx) => (
            <motion.div key={r.id} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: idx * 0.1 }}>
              <Button data-testid={`masak-level-${r.id}`} onClick={() => startLevel(idx)}
                className="w-full h-14 font-fredoka font-bold text-base rounded-xl justify-between px-4"
                style={{ background: `linear-gradient(135deg, hsl(${20 + idx * 30}, 75%, 55%), hsl(${20 + idx * 30}, 75%, 42%))` }}>
                <span className="flex items-center gap-2"><span className="text-xl">{r.result}</span>{r.name}</span>
                <span className="text-xs opacity-80">{r.steps.length} langkah</span>
              </Button>
            </motion.div>
          ))}
        </div>

        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
          <Home className="w-4 h-4 mr-2" />Kembali ke Menu Utama
        </Button>
      </div>
    );
  }

  // WIN
  if (gameState === "won") {
    const isLastLevel = currentLevel >= RECIPES.length - 1;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="masak-bareng-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300}
          colors={["#FFD700", "#FF6347", "#4ECDC4", "#FF69B4", "#87CEEB"]} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
          className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <motion.span className="text-7xl block mb-3"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 1, repeat: 2 }}>
            {recipe.result}
          </motion.span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Makanannya Jadi!</h2>
          <p className="font-fredoka text-gold-light/80 mb-1">Chef {p1} & Chef {p2} hebat!</p>
          <p className="font-fredoka text-gold-light/60 text-sm mb-3">{recipe.resultName}</p>
          <div className="flex justify-center gap-1 mb-4">
            {[0, 1, 2].map(i => <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />)}
          </div>
          <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 mb-4">
            <span className="text-2xl">👩‍🍳</span>
            <span className="font-fredoka text-gold font-bold text-sm">Stiker Chef!</span>
          </div>
          <div className="flex flex-col gap-2">
            {!isLastLevel && (
              <Button onClick={() => startLevel(currentLevel + 1)}
                className="w-full font-fredoka font-bold text-base rounded-xl h-12 bg-gradient-to-r from-green-400 to-emerald-500"
                data-testid="masak-next-level">
                Resep Berikutnya
              </Button>
            )}
            <Button onClick={() => startLevel(currentLevel)}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"
              data-testid="masak-play-again">
              <RotateCcw className="w-4 h-4 mr-2" />Masak Lagi
            </Button>
            <Button onClick={() => setGameState("menu")} variant="outline"
              className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
              Pilih Resep
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PLAYING
  const step = recipe.steps[currentStep];

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="masak-bareng-game">
      {/* Header */}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🍳 {recipe.name}</span>
          <span className="font-fredoka text-gold-light/60 text-xs">
            Langkah {currentStep + 1}/{recipe.steps.length}
          </span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 w-7 p-0">
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          </Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 px-2">
            <Home className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Kitchen Area - 3D */}
      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-amber-400/30 game-area-3d"
        style={{
          minHeight: Math.min(winH - 160, 450),
          background: "linear-gradient(180deg, #FFF8E1 0%, #FFECB3 40%, #FFE0B2 70%, #D7CCC8 100%)",
        }}>
        {/* Three.js 3D Background */}
        <KitchenScene3D />

        {/* Kitchen wall decorations */}
        <motion.div className="absolute top-[10%] left-[5%] text-2xl select-none opacity-40"
          animate={{ rotate: [0, 3, -3, 0] }} transition={{ duration: 4, repeat: Infinity }}>🧂</motion.div>
        <motion.div className="absolute top-[10%] right-[5%] text-2xl select-none opacity-40"
          animate={{ rotate: [0, -3, 3, 0] }} transition={{ duration: 3.5, repeat: Infinity }}>🫙</motion.div>
        <motion.div className="absolute top-[8%] left-[20%] text-xl select-none opacity-30"
          animate={{ y: [0, -2, 0] }} transition={{ duration: 3, repeat: Infinity }}>🥄</motion.div>
        <motion.div className="absolute top-[8%] right-[20%] text-xl select-none opacity-30"
          animate={{ y: [0, -2, 0] }} transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}>🍴</motion.div>

        {/* Recipe card at top - enhanced */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-[90%] max-w-md">
          <motion.div className="rounded-xl p-3 text-center"
            style={{ background: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}
            animate={{ scale: feedback?.type === 'correct' ? [1, 1.03, 1] : 1 }}
            transition={{ duration: 0.3 }}>
            <motion.p className="font-fredoka text-amber-800 font-bold text-base mb-1"
              key={currentStep}
              initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
              {step.instruction}
            </motion.p>
            <div className="flex items-center justify-center gap-2">
              <span className={`font-fredoka text-xs px-2 py-0.5 rounded-full ${currentPlayer === 0 ? 'bg-cyan-400 text-white' : 'bg-gray-200 text-gray-500'}`}>
                👩‍🍳 {p1}
              </span>
              <span className="text-xs text-gray-400">{currentPlayer === 0 ? 'pilih bahan' : 'konfirmasi'}</span>
              <span className={`font-fredoka text-xs px-2 py-0.5 rounded-full ${currentPlayer === 1 ? 'bg-pink-400 text-white' : 'bg-gray-200 text-gray-500'}`}>
                👩‍🍳 {p2}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Progress bar - animated */}
        <div className="absolute top-[60px] left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {recipe.steps.map((_, i) => (
            <motion.div key={i}
              className={`w-8 h-2.5 rounded-full ${i < currentStep ? 'bg-green-400' : i === currentStep ? 'bg-amber-400' : 'bg-gray-300'}`}
              animate={i === currentStep ? { scale: [1, 1.15, 1] } : {}}
              transition={{ duration: 1, repeat: Infinity }} />
          ))}
        </div>

        {/* Completed items on table */}
        <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          {/* Steam from cooking */}
          {completedItems.length > 0 && (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-2">
              {[0, 1, 2].map(i => (
                <motion.span key={`steam-${i}`} className="text-lg opacity-20 select-none"
                  animate={{ y: [0, -15, -30], opacity: [0.25, 0.12, 0], scale: [0.8, 1.2, 1.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}>
                  💨
                </motion.span>
              ))}
            </div>
          )}
          {/* Table/counter - 3D */}
          <motion.div className="rounded-2xl px-8 py-4 flex items-center gap-3 justify-center table-3d"
            style={{ background: 'linear-gradient(180deg, #8D6E63, #6D4C41)', minWidth: '220px' }}
            animate={feedback?.type === 'correct' ? { y: [0, -4, 0] } : {}}
            transition={{ duration: 0.3 }}>
            {completedItems.length === 0 ? (
              <motion.span className="text-3xl opacity-30"
                animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>🍽️</motion.span>
            ) : (
              completedItems.map((item, i) => (
                <motion.span key={i} className="text-3xl"
                  initial={{ scale: 0, y: -30, rotate: -180 }}
                  animate={{ scale: 1, y: 0, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200 }}>
                  {item}
                </motion.span>
              ))
            )}
            {selectedItem && (
              <motion.span className="text-3xl"
                initial={{ scale: 0, rotate: 90 }}
                animate={{ scale: [0, 1.4, 1], rotate: [90, -10, 0] }}
                style={{ filter: 'drop-shadow(0 0 12px rgba(0,200,255,0.7))' }}>
                {selectedItem}
              </motion.span>
            )}
          </motion.div>
          {/* Target result */}
          <motion.div className="text-center mt-2"
            animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity }}>
            <span className="text-4xl" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))' }}>{recipe.result}</span>
          </motion.div>
        </div>

        {/* Chef avatars - 3D enhanced */}
        <div className="absolute bottom-[25%] left-[8%] z-10 text-center char-3d">
          <motion.span className="text-5xl block"
            animate={currentPlayer === 0
              ? { y: [0, -8, 0], rotate: [0, -5, 5, 0] }
              : { y: [0, -2, 0] }}
            transition={currentPlayer === 0
              ? { duration: 0.6, repeat: Infinity }
              : { duration: 2, repeat: Infinity }}>
            👩‍🍳
          </motion.span>
          <motion.span className={`font-fredoka text-xs font-bold block ${currentPlayer === 0 ? 'text-cyan-600' : 'text-gray-400'}`}
            animate={currentPlayer === 0 ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 1, repeat: Infinity }}>
            {p1}
          </motion.span>
        </div>
        <div className="absolute bottom-[25%] right-[8%] z-10 text-center char-3d">
          <motion.span className="text-5xl block"
            animate={currentPlayer === 1
              ? { y: [0, -8, 0], rotate: [0, 5, -5, 0] }
              : { y: [0, -2, 0] }}
            transition={currentPlayer === 1
              ? { duration: 0.6, repeat: Infinity }
              : { duration: 2, repeat: Infinity, delay: 0.5 }}>
            👩‍🍳
          </motion.span>
          <motion.span className={`font-fredoka text-xs font-bold block ${currentPlayer === 1 ? 'text-pink-600' : 'text-gray-400'}`}
            animate={currentPlayer === 1 ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 1, repeat: Infinity }}>
            {p2}
          </motion.span>
        </div>

        {/* Feedback - enhanced with sparkles */}
        <AnimatePresence>
          {feedback && (
            <motion.div className="absolute top-[38%] left-1/2 -translate-x-1/2 z-30"
              initial={{ scale: 0, y: 30, rotate: -10 }}
              animate={{ scale: [0, 1.2, 1], y: 0, rotate: 0 }}
              exit={{ scale: 0, opacity: 0, y: -20 }}>
              <div className={`font-fredoka font-bold text-xl px-8 py-3 rounded-2xl shadow-lg ${feedback.type === 'correct' ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' : 'bg-gradient-to-r from-red-400 to-rose-500 text-white'}`}>
                {feedback.type === 'correct' ? '✅ Benar!' : '❌ Coba lagi!'}
              </div>
              {feedback.type === 'correct' && [0, 1, 2, 3].map(i => (
                <motion.span key={`fb-${i}`} className="absolute text-xl"
                  style={{ top: '50%', left: '50%' }}
                  initial={{ x: 0, y: 0, opacity: 1 }}
                  animate={{
                    x: Math.cos(i * 1.57) * 50,
                    y: Math.sin(i * 1.57) * 50 - 20,
                    opacity: 0, scale: 0,
                  }}
                  transition={{ duration: 0.6, delay: 0.1 }}>
                  ✨
                </motion.span>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kitchen decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-[15%]"
          style={{ background: 'linear-gradient(180deg, #BCAAA4, #8D6E63)' }}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-amber-600/30" />
        </div>
      </div>

      {/* Ingredients selection */}
      <div className="w-full mt-3">
        <p className="font-fredoka text-gold-light/60 text-xs text-center mb-2">
          {currentPlayer === 0 ? `👩‍🍳 ${p1}: Pilih bahan yang benar!` : `👩‍🍳 ${p2}: Tap bahan untuk konfirmasi!`}
        </p>
        <div className="grid grid-cols-4 gap-2 sm:gap-3 scene-3d">
          {recipe.ingredients.map((ing, idx) => (
            <motion.button key={`${ing}-${idx}`}
              data-testid={`masak-ingredient-${idx}`}
              onClick={() => handleIngredientClick(ing)}
              initial={{ scale: 0, rotate: -20 }}
              animate={{
                scale: wrongItem === ing ? [1, 0.9, 1.1, 0.9, 1] : 1,
                rotate: wrongItem === ing ? [0, -5, 5, -5, 0] : 0,
              }}
              transition={{ scale: { delay: idx * 0.05, duration: 0.3 } }}
              className={`ingredient-3d aspect-square rounded-2xl flex items-center justify-center text-4xl sm:text-5xl
                ${wrongItem === ing ? 'ring-4 ring-red-400 !bg-red-100' : ''}
                ${selectedItem === ing ? 'ring-4 ring-cyan-400 !bg-cyan-50' : ''}
              `}
              whileTap={{ scale: 0.85, rotateX: 5 }}>
              {ing}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MasakBareng;
